/**
 * Primary nav (frontend-map §2.1, §6.1 — FOUNDATION-owned).
 * Three destinations: Library, Activities, Trips.
 *
 * Responsive: inline links on md+; below that a hamburger opens a slide-over
 * sheet from the right with full-size touch targets. The user menu stays visible
 * at every size.
 *
 * Feature agents link to these as string targets; they never edit this file.
 */
import { useMe } from '@/api/me';
import { authAdapter } from '@/auth';
import { ChangesDialog } from '@/components/trips/ChangesDialog';
import { QuickAddNavButton } from '@/components/trips/QuickAddNavButton';
import { tripItemEventTimeAgo } from '@/components/trips/TripItemActivity';
import { type ChangeGroup, countLines, summarizeChanges } from '@/components/trips/changeSummary';
import { PulseDot } from '@/components/ui/PulseDot';
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { forceReloadApp } from '@/lib/pwa';
import { applyRemoteChanges } from '@/lib/useRemoteTripChanges';
import { cn } from '@/lib/utils';
import { useCommandsStore } from '@/stores/commands';
import { useQuickAddStore } from '@/stores/quickAdd';
import { useTripChangesStore } from '@/stores/tripChanges';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  Command,
  ListChecks,
  Menu,
  Plane,
  RefreshCw,
  RotateCw,
  Settings,
  Shield,
  Sparkles,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

const { UserMenu } = authAdapter;

const NAV_ITEMS = [
  { to: '/library', label: 'Library', icon: ListChecks },
  { to: '/activities', label: 'Activities', icon: Sparkles },
  { to: '/trips', label: 'Trips', icon: Plane },
  { to: '/settings', label: 'Settings', icon: Settings },
] as const;

/** Admin gets one extra destination, surfaced only when `me.isAdmin`. */
const ADMIN_ITEM = { to: '/admin', label: 'Admin', icon: Shield } as const;

const desktopItem =
  'flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors';
const sheetItem =
  'flex items-center gap-3 rounded-xl px-3 py-3 text-base font-medium transition-colors active:bg-[var(--accent)]';

/** The two most-used destinations, surfaced as inline header links on md+. */
const QUICK_LINKS = NAV_ITEMS.filter((item) => item.to === '/library' || item.to === '/trips');

export function AppNav() {
  const [open, setOpen] = useState(false);
  const me = useMe();
  const navItems = me.data?.isAdmin ? [...NAV_ITEMS, ADMIN_ITEM] : NAV_ITEMS;
  const openPalette = useCommandsStore((state) => state.setOpen);
  const pending = useTripChangesStore((state) => state.pending);
  const applied = useTripChangesStore((state) => state.applied);
  const clearApplied = useTripChangesStore((state) => state.clearApplied);
  const onTrip = useTripChangesStore((state) => state.tripId !== null);
  const checkNow = useTripChangesStore((state) => state.checkNow);
  const checking = useTripChangesStore((state) => state.checking);
  const lastCheckedAt = useTripChangesStore((state) => state.lastCheckedAt);
  const [changesOpen, setChangesOpen] = useState(false);
  /** Inline up to this many lines; beyond it, a count + "View all". */
  const INLINE_MAX = 3;
  const pendingChanges = pending.length;
  const pendingGroups = summarizeChanges(pending);
  const appliedGroups = summarizeChanges(applied);
  const qc = useQueryClient();
  const [applying, setApplying] = useState(false);

  // Tap a change line: close the menu and scroll that list into view.
  async function onApplyChanges() {
    setApplying(true);
    try {
      await applyRemoteChanges(qc);
      setChangesOpen(false);
      setOpen(false);
    } finally {
      setApplying(false);
    }
  }

  async function onCheckNow() {
    if (!checkNow) {
      return;
    }
    const before = useTripChangesStore.getState().pending.length;
    await checkNow();
    const after = useTripChangesStore.getState().pending.length;
    if (after === before) {
      toast(after === 0 ? 'You’re up to date' : 'Nothing new since last check', {
        position: 'top-center',
      });
    }
  }

  return (
    <nav className="flex items-center gap-1">
      {/* md+ : inline links */}
      {/* Quick links to the two most-used pages on md+; the rest live in the menu. */}
      <div className="hidden items-center gap-1 md:flex">
        {QUICK_LINKS.map(({ to, label, icon: Icon }) => (
          <Link
            key={to}
            to={to}
            className={cn(
              desktopItem,
              'text-[var(--muted-foreground)] hover:bg-[var(--accent)] hover:text-[var(--accent-foreground)]',
            )}
            activeProps={{
              className: cn(desktopItem, 'bg-[var(--accent)] text-[var(--accent-foreground)]'),
            }}
          >
            <Icon className="h-4 w-4" />
            <span>{label}</span>
          </Link>
        ))}
      </div>

      {/* Slide-over menu (all destinations + account) at every size. */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger
          aria-label={pendingChanges > 0 ? 'Open menu — changes waiting to apply' : 'Open menu'}
          className="relative inline-flex h-10 w-10 items-center justify-center rounded-lg text-[var(--foreground)] transition-colors hover:bg-[var(--accent)] hover:text-[var(--accent-foreground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
        >
          <Menu className="h-5 w-5" />
          {/* Someone else changed the open trip — the same dot as on the list header. */}
          {pendingChanges > 0 ? (
            <PulseDot className="absolute top-1.5 right-1.5" label="Changes waiting to apply" />
          ) : null}
        </SheetTrigger>
        <SheetContent
          side="right"
          className="w-72"
          onCloseAutoFocus={(event) => {
            // When the menu closes to launch Quick Add, don't restore focus to the
            // hamburger — that would steal it back from the modal's text field.
            if (useQuickAddStore.getState().open) {
              event.preventDefault();
            }
          }}
        >
          {/* Quick add sits beside the title (trip pages only) so it's right under
              the thumb after the hamburger opens — minimal finger travel. Right
              padding keeps it clear of the sheet's absolute X close button. */}
          <div className="flex items-center justify-between gap-2 pt-1 pr-14 pl-3">
            <SheetTitle className="font-display text-lg font-semibold tracking-tight">
              Menu
            </SheetTitle>
            <QuickAddNavButton onLaunch={() => setOpen(false)} />
          </div>
          {/* Held-back edits by others: nothing on the board moves until Apply is
              tapped. Applying is all-or-nothing; the panel only says what's waiting. */}
          {pendingChanges > 0 ? (
            <div className="mt-3 overflow-hidden rounded-xl border border-[var(--primary)] bg-[var(--card)]">
              <button
                type="button"
                onClick={() => void onApplyChanges()}
                disabled={applying}
                className="flex w-full items-center gap-3 bg-[var(--accent)] px-3 py-3 text-left font-medium text-[var(--accent-foreground)] text-base transition-colors hover:opacity-90 disabled:opacity-60"
              >
                <RefreshCw className={cn('h-5 w-5', applying && 'animate-spin')} />
                <span className="flex-1">
                  {applying
                    ? 'Applying…'
                    : `Apply ${pendingChanges} ${pendingChanges === 1 ? 'change' : 'changes'}`}
                </span>
                <PulseDot label="" />
              </button>
              {countLines(pendingGroups) <= INLINE_MAX ? (
                <div className="border-[var(--border)] border-t px-3 pt-2 pb-1">
                  <p className="font-semibold text-[11px] text-[var(--muted-foreground)] uppercase tracking-wide">
                    What changed
                  </p>
                  <ChangeList groups={pendingGroups} bare />
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setChangesOpen(true)}
                  className="flex w-full items-center justify-between gap-2 border-[var(--border)] border-t px-3 py-2 text-left text-sm hover:bg-[var(--accent)]"
                >
                  <span className="text-[var(--muted-foreground)]">
                    {countLines(pendingGroups)} changes across {pendingGroups.length}{' '}
                    {pendingGroups.length === 1 ? 'list' : 'lists'}
                  </span>
                  <span className="font-medium text-[var(--primary)]">View all</span>
                </button>
              )}
            </div>
          ) : applied.length > 0 ? (
            <div className="mt-3 rounded-xl border border-[var(--border)] px-3 py-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[11px] font-semibold text-[var(--muted-foreground)] uppercase tracking-wide">
                  Recently applied
                </span>
                <button
                  type="button"
                  onClick={clearApplied}
                  className="font-medium text-[var(--primary)] text-xs hover:underline"
                >
                  Clear
                </button>
              </div>
              <ChangeList groups={appliedGroups} bare />
            </div>
          ) : null}
          {onTrip ? (
            <button
              type="button"
              onClick={() => void onCheckNow()}
              disabled={checking}
              className="mt-2 flex min-h-9 items-center gap-2 rounded-lg px-3 text-[var(--muted-foreground)] text-xs hover:bg-[var(--accent)] hover:text-[var(--accent-foreground)] disabled:opacity-60"
            >
              <RefreshCw className={cn('h-3.5 w-3.5', checking && 'animate-spin')} />
              <span className="whitespace-nowrap">
                {checking ? 'Checking…' : 'Check for changes now'}
              </span>
              {lastCheckedAt && !checking ? (
                <span className="ml-auto whitespace-nowrap opacity-70">
                  {tripItemEventTimeAgo(new Date(lastCheckedAt).toISOString())}
                </span>
              ) : null}
            </button>
          ) : null}
          <div className="mt-3 flex flex-col gap-1">
            {/* The command palette — ⌘K on a keyboard, this on a phone. */}
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                // Let the menu close first so the palette gets focus.
                requestAnimationFrame(() => openPalette(true));
              }}
              className={cn(
                sheetItem,
                'text-[var(--foreground)] hover:bg-[var(--accent)] hover:text-[var(--accent-foreground)]',
              )}
            >
              <Command className="h-5 w-5" />
              <span className="flex-1 text-left">Commands…</span>
              <kbd className="hidden rounded border border-[var(--border)] bg-[var(--muted)] px-1.5 py-0.5 font-mono text-[10px] text-[var(--muted-foreground)] md:inline">
                ⌘K
              </kbd>
            </button>
            {navItems.map(({ to, label, icon: Icon }) => (
              <Link
                key={to}
                to={to}
                onClick={() => setOpen(false)}
                className={cn(
                  sheetItem,
                  'text-[var(--foreground)] hover:bg-[var(--accent)] hover:text-[var(--accent-foreground)]',
                )}
                activeProps={{
                  className: cn(sheetItem, 'bg-[var(--accent)] text-[var(--accent-foreground)]'),
                }}
              >
                <Icon className="h-5 w-5" />
                <span>{label}</span>
              </Link>
            ))}
          </div>

          {/* Footer, below the divider: account on the left, a quiet Refresh on
              the right. Refresh is deliberately out of the main list — rarely
              needed, but one tap away on any device when a stale bundle bites. */}
          <div className="mt-auto border-[var(--border)] border-t pt-3">
            <div className="flex items-center justify-between gap-3 px-3 py-2">
              <div className="flex items-center gap-3">
                <UserMenu />
                <span className="text-[var(--muted-foreground)] text-sm">Account</span>
              </div>
              <button
                type="button"
                onClick={() => void forceReloadApp()}
                className="inline-flex h-11 items-center gap-1.5 rounded-lg px-3 text-[var(--muted-foreground)] text-sm transition-colors hover:bg-[var(--accent)] hover:text-[var(--accent-foreground)] md:h-9"
                title="Reload the app to pick up the latest version"
              >
                <RotateCw className="h-4 w-4" />
                Refresh
              </button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
      <ChangesDialog
        open={changesOpen}
        onOpenChange={setChangesOpen}
        groups={pendingGroups}
        count={pendingChanges}
        applying={applying}
        onApply={() => void onApplyChanges()}
      />
    </nav>
  );
}

/** Change lines grouped by list — a read-only summary, nothing to act on per line. */
function ChangeList({
  groups,
  bare = false,
  max = 8,
}: {
  groups: ChangeGroup[];
  bare?: boolean;
  max?: number;
}) {
  const total = groups.reduce((sum, group) => sum + group.lines.length, 0);
  let shown = 0;
  return (
    <div className={cn('flex flex-col gap-2 text-sm', bare ? 'mt-2' : 'mt-2 px-1')}>
      {groups.map((group) => {
        if (shown >= max) {
          return null;
        }
        const lines = group.lines.slice(0, max - shown);
        shown += lines.length;
        return (
          <div key={group.listKey} className="flex flex-col gap-0.5">
            <span className="text-[var(--muted-foreground)] text-xs">{group.listName}</span>
            {lines.map((line) => (
              <p key={line.key} className="px-2 py-1 leading-snug">
                {line.text}
              </p>
            ))}
          </div>
        );
      })}
      {total > max ? (
        <span className="px-2 text-[var(--muted-foreground)] text-xs">and {total - max} more</span>
      ) : null}
    </div>
  );
}
